<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Awards from '$lib/PageSections/Awards/Awards.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import acousticGradeImg from './images/acousticGrade.png';
	import caulkingImg from './images/caulking.jpeg';
	import doublePanedWindowImg from './images/doublePanedWindow.jpg';
	import manLookingOutWindow from './images/manLookingOutWindow.jpg';
	import soundproofCurtainsImg from './images/soundproofCurtains.jpeg';
	import windowTapeImg from './images/windowTape.jpeg';
</script>

<Section>
	<p>
		For most homeowners, home is a haven. It’s your space that looks, feels and
		even smells exactly how you’ve chosen it to. But in heavily populated areas
		or homes near things like airports, train tracks, or busy intersections,
		outside noise can disrupt your flow and bring in unwanted distractions. To
		help with noise reduction, homeowners frequently decide to invest in new
		windows from Home Depot and other retailers.
	</p>
	<p>
		However, you might not need to completely replace your windows to help keep
		your home quiet and comfortable.
	</p>
	<div class="flex flex-col items-center gap-3">
		<Image
			src={manLookingOutWindow}
			alt="man sitting at a desk looking out the window"
		/>
		<a href="https://unsplash.com/photos/p8DjPfqEhW0">Source</a>
	</div>
	<h2>
		Why Should I <a class="px-0" href="/resources/soundproofing-windows"
			>Soundproof My Windows?</a
		>
	</h2>
	<p>
		Acoustics matter in your home, but it’s not the first thing people think of
		when they’re designing their space—quite often, the way sound travels in
		your space is an afterthought.
	</p>
	<p>
		But better acoustics in the home adds to indoor comfort and helps humans
		actually enjoy their space.
	</p>
	<p>
		“Acoustics often remain a neglected aspect of interior design and
		architecture,” says Sudeep Kolte at <a
			href="https://housing.com/news/style-friendly-ways-improve-homes-acoustics"
			>Housing.com</a
		>. “Although we may not think of the sound and acoustics of our home, it
		remains an integral aspect towards greater comfort and a better ambiance.”
	</p>
	<p>
		Studies have shown that an acoustically comfortable home can help to make
		things more pleasant for your family. The benefits go beyond a peaceful,
		serene space, though. A quiet home helps with better sleep, improved mental
		health, and even <a
			href="https://www.buildings.com/articles/27847/acoustics-and-impact-sound-health"
			>lower blood pressure</a
		>.
	</p>
</Section>

<Section>
	<h2>Noise Reduction Windows and Other Sound Solutions</h2>
	<p>
		Home Depot and other large retailers offer homeowners a few products to make
		their homes more <a href="/resources/blog/soundproof-home-office"
			>acoustically comfortable</a
		>. It’s important to note that while no windows are 100% soundproof, there
		are a few solutions that might help to drastically reduce the noise in your
		space.
	</p>
	<div class="flex flex-col items-center gap-3">
		<div class="flex flex-col gap-8">
			<h3>Double-Paned windows</h3>
			<Image src={doublePanedWindowImg} alt="double-paned window" />
		</div>
		<a
			href="https://www.finehomebuilding.com/2016/09/14/tough-choice-double-pane-vs-triple-pane-windows"
			>Source</a
		>
	</div>
	<p>
		Double-panes are often the easy solution that is available and bought at big
		box stores, but they are not the best for sound performance. Plus, fully
		replacing all of the old single-paned windows on a home isn’t always a
		feasible option for homeowners.
	</p>
	<p>
		High-performing double-pane windows can be pricey, so you might not be able
		to replace all of the windows at once, and even then, they may not provide
		the level of sound reduction you actually want.
	</p>
	<p>
		While they are better than single-paned windows — because there are more
		surfaces for sound to travel through and the air between the panes acts as
		another buffer for noise — they may not be your best choice, especially if
		you have a historic home.
	</p>
	<p>
		Some homeowners may want to keep the original, single-pane windows if they
		have a historic home, in order to preserve the architectural character. Many
		older windows are made of irreplaceable old-growth wood and replacing them
		with vinyl means the new windows will not aesthetically match the home. So
		instead of replacing the historic window, <a
			href="/resources/blog/storm-window-alternative"
			>it’s suggested that homeowners opt</a
		>
		for repairs and the addition of a storm window insert. These can be the best noise
		reduction option from historic windows to newly installed double-panes.
	</p>
	<p>
		If you’re building a new home, choosing double-paned windows is a smart
		choice. If you have single-paned windows in your current home and you can’t
		replace all of them at the same time, see if you can work with a contractor
		to replace them in stages.
	</p>
	<p>
		Or, to save on time, labor and money, you can use other implements with your
		existing windows in order to reduce sound travel.
	</p>
	<div class="flex flex-col items-center gap-3">
		<div class="flex flex-col gap-8">
			<h3>Window Tape</h3>
			<Image src={windowTapeImg} alt="package of window tape" />
		</div>
		<a
			href="https://www.homedepot.com/p/Protecto-Wrap-9-in-x-33-3-in-Window-and-Door-Tape-123122/202329102?NCNI-5#product-overview"
			>Source</a
		>
	</div>
	<p>
		Home Depot offers a few budget-friendly options for noise reduction window
		products, like Protecto tape. The tape acts as an all-in-one product that
		helps to prevent air and moisture leaks around windows and doors, and a roll
		of it comes in around $20. This particular product needs to be added when
		your window is installed. Other products, like <a
			href="https://www.homedepot.com/p/Frost-King-3-4-in-x-7-16-in-x-10-ft-Black-High-Density-Rubber-Foam-Weatherstrip-Tape-R734H/100047977"
			>weatherstip foam tape</a
		>, can be added after your window is installed.
	</p>
	<p>
		Tape products like Protecto are an inexpensive, quick, and easy way to help
		provide some soundproofing, but it’s not going to cancel out much of the
		sound coming into your space because there is no added insulation to the
		window opening. You’ll need to combine it with other products to help
		effectively block unwanted noise entering the home.
	</p>
	<div class="flex flex-col items-center gap-3">
		<div class="flex flex-col gap-8">
			<h3>Soundproofing Curtains</h3>
			<Image
				src={soundproofCurtainsImg}
				alt="Soundproofing black out curtains"
			/>
		</div>
		<a
			href="https://www.homedepot.com/p/DUCK-RIVER-TEXTILE-Stripe-Rust-Polyester-Blackout-Grommet-Window-Curtain-38-in-W-x-84-in-L-2-Pack-LYSANNA-10727D-12/310630411"
			>Source</a
		>
	</div>
	<p>
		Curtains are frequently recommended as a solution for noise-reduction in the
		home. Soft surfaces and fabrics can help to absorb noise in your space, and
		they can add a touch of style to your interior design at the same time.
	</p>
	<p>
		Soundproofing curtains can run anywhere from $30-100. If you don’t want to
		completely replace your windows, the curtains act as a cost-effective
		alternative. As a bonus, most soundproofing curtains are also blackout
		curtains, which can reduce the amount of light entering the room, helping
		occupants sleep more comfortably.
	</p>
	<p>
		However, while these types of curtains are better than uncovered windows,
		they are not a complete solution. Sound and light will still get through, no
		matter how high-quality the curtains are.
	</p>
	<div class="flex flex-col items-center gap-3">
		<div class="flex flex-col gap-8">
			<h3>Caulking</h3>
			<Image
				src={caulkingImg}
				alt="caulk gun used to caulk and insulate windows"
			/>
		</div>
		<a href="https://commons.wikimedia.org/wiki/File:Caulking.jpg">Source</a>
	</div>
	<p>
		Many home improvement professionals recommend caulking as a way to block out
		sound—not just around windows, but other areas in your home that might let
		it noise.
	</p>
	<p>
		“To reduce sound coming through walls and floors, caulk around light
		fixtures, receptacle boxes, and door casings,” writes Lisa Kaplan Gordon <a
			href="https://www.houselogic.com/remodel/windows-doors-and-floors/soundproof-windows"
			>for Houselogic</a
		>.
	</p>
	<p>
		Caulking isn’t going to prevent all noise from coming into your home, and
		you’ll need to re-caulk those areas again in the future. Plus, if you don’t
		pick just the right color, it can be an eyesore—especially in a few months
		or years when it starts to crack from age.
	</p>
	<p>
		But, by including caulking in your <a
			href="/resources/soundproofing-windows">soundproofing strategy</a
		>, you’ll add to the acoustic comfort in your home, without hurting your
		budget.
	</p>
</Section>

<Section>
	<h2>Window Inserts</h2>
	<p>
		If you don’t want to replace your old windows and options such as installing
		curtains, caulking, and window tape sound like a hassle or don’t meet your
		needs, you’re in luck. Window inserts are a great solution to help <a
			href="/resources/soundproofing-windows"
			>reduce the amount of noise entering your home</a
		>.
	</p>
	<Image
		src={acousticGradeImg}
		alt="woman installing soundproof window insert"
	/>
	<p>
		<a href="/solutions/window-noise-reduction"
			>Indow soundproofing window inserts</a
		> are an effective and easy-to-install noise reduction product. The inserts are
		custom-made to fit each of your windows exactly, and they are then installed by
		pressing them in your window frames—no mounting brackets or adhesives needed.
	</p>
	<p>
		All Indow window inserts reduce outside noise by at least 50%, and the
		Acoustic Grade product reduces noise by up to 70%*.
	</p>
</Section>

<Section>
	<h2>Don’t Make Acoustic Design an Afterthought</h2>
	<p>
		If your home isn’t feeling like the escape that you need it to be,
		soundproofing products can help to make things more comfortable. Thankfully,
		you don’t need to completely redesign your home to make it acoustically
		comfortable.
	</p>
	<p>
		Window inserts from Indow can help you achieve an indoor environment you’ll
		love and a peaceful place for your family. Find out more about <a
			href="/solutions/window-noise-reduction"
			>Indow Soundproofing Window Inserts</a
		>
		here, or contact Indow for
		<a href="/how-to/get-indow-inserts">a free estimate</a>.
	</p>
	<p class="italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
</Section>

<GetAFreeEstimate title="Let Us Help You" />
<Awards backgroundColor="grey-light" />
<Reviews />
